import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { RestService } from './rest.service';
import {
  ApiResponseMultiple,
  ApiResponseSingle,
  BaseApiResponse,
  LdAccountApiResponses,
} from '../../models';
import {
  CampaignListRequestPayload,
  CampaignRecipientsListRequestPayload,
  CampaignStatistics,
  CampaignStatisticsResponse,
  CampaignStatus,
} from '../../models/campaign/campaign';
import {
  CampaignCountRecipientsRequest,
  CampaignCreationPayload,
  CampaignEmailTemplate,
  CampaignLinkedinCredential,
  CampaignSchedule,
  CampaignSequenceMap,
  EmailContentSavePayload,
  EmailFilterItem,
  EmailFiltersPageInfo,
  EmailFilterValueItemPayload,
  GeoCodeInfo,
  SequenceNodeEmailVariant,
} from '../../models/campaign/campaign-settings';
import {
  CampaignEmailCredential,
  EmailCredentialSummaryResponse,
} from '../../models/campaign/campaign-account';
import { FileAttachmentMetaInfo } from 'src/app/modules/campaign/modules/flowchart/shared/types/attachment';
import { SelectionOption } from 'src/app/modules/campaign/modules/campaign-shared-module/types/selection';
import { EmailNodeVariationContent } from 'src/app/modules/campaign/modules/flowchart/shared/types';
import { LinkedinAddAccountRequest } from '../../models/user/linkedinAccount';
import { CampaignDetailInfo } from 'src/app/modules/campaign/modules/campaign-analytics/shared/models/campaign-analytics';
import { CampaignRecipientsResponse } from 'src/app/modules/campaign/modules/campaign-analytics/shared/models';
import { LinkedinCredentialMessagesResponse } from '../../../modules/system-shared/models/profile-messages.model';
import { ProfileFilter } from 'src/app/modules/campaign/modules/campaign-analytics/shared/models';
import { SortOrderType } from '../../types';
import { getQueryStringFromParams } from 'src/app/modules/system-shared/utils/utilities';

@Injectable({
  providedIn: 'root',
})
export class CampaignService {
  constructor(private restService: RestService) {}

  getCampaignsByUser(): Observable<CampaignStatistics[]> {
    return this.restService
      .Campaign()
      .getCampaignByUser()
      .pipe(
        map((response: ApiResponseMultiple<CampaignStatistics>) => {
          return RestService.getData(response) || [];
        }),
      );
  }

  getAllCampaigns(params: CampaignListRequestPayload): Observable<CampaignStatisticsResponse> {
    const query = `?page=${params.page}&size=${params.size}&order=${params.sort}`;
    return this.restService.Campaign().getAllCampaigns(query);
  }

  getCampaignById(id: string): Observable<CampaignCreationPayload> {
    return this.restService.Campaign().getCampaignById(id);
  }

  createNewCampaign(campaign: CampaignCreationPayload): Observable<CampaignStatistics | string> {
    return this.restService.Campaign().createNewCampaign(campaign);
  }

  editCampaign(campaign: CampaignCreationPayload): Observable<null> {
    return this.restService.Campaign().editCampaign(campaign);
  }

  archiveCampaign(id: string): Observable<null> {
    return this.restService.Campaign().archiveCampaign(id);
  }

  completeCampaign(id: string): Observable<null> {
    return this.restService.Campaign().completeCampaign(id);
  }

  toggleCampaign(id: string): Observable<CampaignStatus> {
    return this.restService.Campaign().toggleCampaign(id);
  }

  deleteCampaign(id: string): Observable<BaseApiResponse> {
    return this.restService.Campaign().deleteCampaign(id);
  }

  duplicateCampaign(id: string): Observable<null> {
    return this.restService.Campaign().duplicateCampaign(id);
  }

  getEmailFilters(params?: {
    page: number;
    pageSize: number;
    sort: SortOrderType;
  }): Observable<EmailFiltersPageInfo> {
    let query = '';
    if (params) {
      query = `?page=${params.page - 1}`;
      query += `&size=${params.pageSize}`;
      query += `&sort=${params.sort}`;
    }

    return this.restService.Campaign().getEmailFilters(query);
  }

  createEmailFilter(filter: { name: string }): Observable<any> {
    return this.restService.Campaign().createEmailFilter(filter);
  }

  getEmailFilterById(id: string): Observable<EmailFilterItem> {
    return this.restService.Campaign().getEmailFilterById(id);
  }

  editEmailFilterName(id: string, newName: string): Observable<null> {
    return this.restService.Campaign().changeEmailFilter(id, { name: newName });
  }

  deleteEmailFilter(id: string): Observable<null> {
    return this.restService.Campaign().deleteEmailFilter(id);
  }

  addValuesToEmailFilter(item: EmailFilterValueItemPayload): Observable<null> {
    return this.restService.Campaign().addValuesToEmailFilter(item);
  }

  editValueOfEmailFilter(item: EmailFilterValueItemPayload): Observable<any> {
    return this.restService.Campaign().editValueOfEmailFilter(item);
  }

  deleteValueOfEmailFilter(id: string): Observable<null> {
    return this.restService.Campaign().deleteValueOfEmailFilter(id);
  }

  getProspectsCount(info: CampaignCountRecipientsRequest): Observable<number> {
    return this.restService.Campaign().getRecepientsCount(info);
  }

  saveNewSchedule(schedule: CampaignSchedule): Observable<any> {
    return this.restService.Campaign().saveNewSchedule(schedule);
  }

  updateSchedule(schedule: CampaignSchedule): Observable<any> {
    return this.restService.Campaign().updateSchedule(schedule);
  }

  getScheduleList(): Observable<CampaignSchedule[]> {
    return this.restService.Campaign().getScheduleList();
  }

  deleteSchedule(id: string): Observable<any> {
    return this.restService.Campaign().deleteSchedule(id);
  }

  getAvailableZoneIds(): Observable<string[]> {
    return this.restService.Campaign().getAvailableZoneIds();
  }

  getEmailCredentials(): Observable<EmailCredentialSummaryResponse[]> {
    return this.restService.Campaign().getEmailCredentials();
  }

  getEmailCredential(id: string): Observable<CampaignEmailCredential> {
    return this.restService.Campaign().getEmailCredential(id);
  }

  updateEmailCredential(credential: CampaignEmailCredential): Observable<null> {
    return this.restService.Campaign().updateEmailCredential(credential);
  }

  saveEmailCredential(credential: CampaignEmailCredential): Observable<any> {
    return this.restService.Campaign().saveEmailCredential(credential);
  }

  deleteEmailCredential(id: string): Observable<null> {
    return this.restService.Campaign().deleteEmailCredential(id);
  }

  verifyEmailAccountCredentials(credential: CampaignEmailCredential): Observable<boolean> {
    return this.restService.Campaign().verifyEmailCredentials(credential);
  }

  verifyTrackingDomain(domainName: string): Observable<boolean> {
    const encodedName = encodeURI(domainName);
    return this.restService.Campaign().verifyTrackingDomain(encodedName);
  }

  getLinkedinCredentials(): Observable<CampaignLinkedinCredential[]> {
    return this.restService.Campaign().getLinkedinCredentials();
  }

  deleteLinkedinCredential(id: string): Observable<null> {
    return this.restService.Campaign().deleteLinkedinCredential(id);
  }

  updateLinkedinCredential(credential: CampaignLinkedinCredential): Observable<null> {
    return this.restService.Campaign().updateLinkedinCredential(credential);
  }

  getGeoCodeList(): Observable<GeoCodeInfo[]> {
    return this.restService.Campaign().getGeoCodeList();
  }

  getLinkedinCredentialMessages(
    credentialId: string,
    docId: string,
  ): Observable<LinkedinCredentialMessagesResponse> {
    return this.restService
      .Campaign()
      .getLinkedinCredentialsMessages(credentialId, docId)
      .pipe(
        map((response: ApiResponseSingle<LinkedinCredentialMessagesResponse>) =>
          RestService.getData(response),
        ),
      );
  }

  connectWithProfile(credentialId: string, docId: string): Observable<null> {
    return this.restService
      .Campaign()
      .connectWithProfile(credentialId, docId)
      .pipe(map((response: ApiResponseSingle<any>) => RestService.getData(response)));
  }

  sendLinkedinMessage(credentialId: string, docId: string, message: string): Observable<null> {
    return this.restService.Campaign().sendLinkedinMessage(credentialId, docId, message);
  }

  getEmailTemplates(): Observable<CampaignEmailTemplate[]> {
    return this.restService.Campaign().getEmailTemplates();
  }

  saveEmailTemplate(email: CampaignEmailTemplate): Observable<CampaignEmailTemplate> {
    return this.restService.Campaign().saveEmailTemplate(email);
  }

  updateEmailTemplate(email: CampaignEmailTemplate): Observable<CampaignEmailTemplate> {
    return this.restService.Campaign().updateEmailTemplate(email);
  }

  deleteEmailTemplate(id: string): Observable<null> {
    return this.restService.Campaign().deleteEmailTemplate(id);
  }

  addEmailAttachment(attachent: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', attachent);

    return this.restService.Campaign().addEmailAttachment(formData);
  }

  getAttachmenInfos(attachmentIds: string[]): Observable<FileAttachmentMetaInfo[]> {
    const query: string = attachmentIds
      .map((id: string, ind: number) => {
        return ind === 0 ? '?ids=' + id : '&ids=' + id;
      })
      .join('');

    return this.restService.Campaign().getAttachmentMetaInfo(query);
  }

  getEmailContent(
    campaignId: string,
    stepId: number,
    variant: SequenceNodeEmailVariant,
  ): Observable<any> {
    return this.restService.Campaign().getEmailContent(campaignId, stepId, variant);
  }

  updateEmailContent(
    campaignId: string,
    stepId: number,
    variant: SequenceNodeEmailVariant,
    emailContent: EmailContentSavePayload,
  ): Observable<any> {
    return this.restService.Campaign().updateEmail(campaignId, stepId, variant, emailContent);
  }

  removeEmail(
    campaignId: string,
    stepId: number,
    variant: SequenceNodeEmailVariant,
  ): Observable<any> {
    return this.restService.Campaign().removeEmail(campaignId, stepId, variant);
  }

  getEmailVariables(): Observable<SelectionOption[]> {
    return this.restService.Campaign().getEmailVariables();
  }

  testEmail(data: EmailNodeVariationContent): Observable<any> {
    return this.restService.Campaign().testEmail(data);
  }

  setMainEmailAccount(id: string): Observable<null> {
    return this.restService.Campaign().setMainEmailAccount(id);
  }

  addLdAccount(ldData: LinkedinAddAccountRequest): Observable<string> {
    return this.restService.Campaign().addLdAccount(ldData);
  }

  addEmailCodeToLdAccount(id: string, emailCode: string): Observable<null> {
    return this.restService.Campaign().addEmailCodeToLdAccount(id, emailCode);
  }

  resendLdAccountCode(id: string): Observable<null> {
    return this.restService.Campaign().resendLdAccountCode(id);
  }

  tryAnotherLdAccountVerificationWay(id: string): Observable<null> {
    return this.restService.Campaign().tryAnotherLdAccountVerificationWay(id);
  }

  checkLdAccountProcessingStatus(id: string): Observable<LdAccountApiResponses> {
    return this.restService.Campaign().checkLdAccountProcessingStatus(id);
  }

  toggleEnableInLdAccount(id: string, enable: boolean): Observable<any> {
    return this.restService.Campaign().toggleEnableInLdAccount(id, enable);
  }

  editPausedCampaign(campaign: CampaignCreationPayload): Observable<null> {
    return this.restService.Campaign().editPausedCampaign(campaign);
  }

  getCampaignAnalyticsAboutInfoById(id: string): Observable<CampaignDetailInfo> {
    return this.restService.Campaign().getCampaignAnalyticsAboutInfoById(id);
  }

  getCampaignAnalyticsOverviewInfoById(id: string): Observable<CampaignSequenceMap> {
    return this.restService.Campaign().getCampaignAnalyticsOverviewInfoById(id);
  }

  getCampaignAnalyticsRecipientsInfoById(
    campaignId: string,
    queryParams: CampaignRecipientsListRequestPayload,
    filterBody: ProfileFilter,
  ): Observable<CampaignRecipientsResponse> {
    const queryString = getQueryStringFromParams(queryParams);

    return this.restService
      .Campaign()
      .getCampaignAnalyticsRecipientsInfoById(campaignId, queryString, filterBody);
  }

  toggleRecipientStatus(campaignId: string, recipientId: string): Observable<CampaignSequenceMap> {
    return this.restService.Campaign().toggleRecipientStatus(campaignId, recipientId);
  }
}
